import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, setImpersonatedOrg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Users, Baby, FileText, MapPin, Eye, LifeBuoy, ArrowLeft, Sparkles, RefreshCw, Plus, Archive, RotateCcw, Trash2 } from "lucide-react";
import PurgeDialog from "@/components/PurgeDialog";

/** /platform — overzicht van alle KDV-organisaties.
 *  Data komt uit tenant-scoped aggregate op /api/platform/overview. */
export function PlatformOverviewPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/platform/overview");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter(i =>
      (i.name || "").toLowerCase().includes(t) ||
      (i.slug || "").toLowerCase().includes(t) ||
      (i.id || "").toLowerCase().includes(t)
    );
  }, [items, q]);

  const kpi = useMemo(() => ({
    total: items.length,
    active: items.filter(i => i.status === "actief").length,
    demo: items.filter(i => i.is_demo).length,
    kids: items.reduce((s, i) => s + (i.counts?.children || 0), 0),
    onb_needs_help: items.filter(i => {
      const p = i.onboarding?.progress;
      const s = i.onboarding?.status;
      return p && p.percent < 100 && s !== "actief" && s !== "active" && s !== "completed";
    }).length,
  }), [items]);

  const startSupport = (org) => {
    setImpersonatedOrg(org.id);
    toast.success(`Supportmodus gestart — ${org.name}`);
    // Hard-navigate to /app so KDV-shell loads fresh with impersonation header.
    setTimeout(() => { window.location.href = "/app"; }, 200);
  };

  const deleteOrg = async (org) => {
    const slug = window.prompt(
      `Weet je zeker dat je "${org.name}" wilt archiveren?\n\n` +
      `De KDV verdwijnt direct uit alle overzichten. Alle data (kinderen, contracten, gebruikers) blijft nog 30 dagen bewaard. ` +
      `Binnen die 30 dagen kun je de KDV herstellen — daarna wordt alles automatisch verwijderd.\n\n` +
      `Typ de slug om te bevestigen: ${org.slug}`
    );
    if (slug !== org.slug) {
      if (slug !== null) toast.error("Bevestiging kwam niet overeen — niets gearchiveerd");
      return;
    }
    try {
      const r = await api.post(`/platform/organizations/${org.id}/archive?confirm=${encodeURIComponent(org.slug)}`);
      const until = (r.data.purge_at || "").slice(0, 10);
      toast.success(`KDV "${org.name}" gearchiveerd — definitieve verwijdering op ${until}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  const restoreOrg = async (org) => {
    if (!window.confirm(`KDV "${org.name}" herstellen? De archivering wordt ongedaan gemaakt.`)) return;
    try {
      await api.post(`/platform/organizations/${org.id}/restore`);
      toast.success(`KDV "${org.name}" hersteld`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="platform-overview">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Platform-overzicht</h1>
          <p className="text-sm text-neutral-500 mt-1">Alle aangesloten KDV-organisaties in Webster</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/platform/organisaties/nieuw")}
            className="text-white"
            style={{ background: "var(--sage)" }}
            data-testid="platform-new-org-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nieuwe KDV kickstarten
          </Button>
          <Button
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            variant="outline"
            data-testid="platform-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Verversen
          </Button>
          <Button
            onClick={() => setPurgeOpen(true)}
            variant="outline"
            className="text-red-700 border-red-300 hover:bg-red-50"
            data-testid="platform-purge-btn"
            title="Verwijder alle records (orgs, users, magic-links) die matchen met een zoekterm"
          >
            <Trash2 className="h-4 w-4 mr-2" /> KDV opruimen
          </Button>
        </div>
      </div>

      <PurgeDialog open={purgeOpen} onOpenChange={setPurgeOpen} onDone={load} />

      {/* KPI-tegels */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="Organisaties" value={kpi.total} icon={Building2} data-testid="kpi-orgs" />
        <Kpi label="Actief" value={kpi.active} icon={Sparkles} data-testid="kpi-active" />
        <Kpi label="Demo's" value={kpi.demo} icon={Sparkles} data-testid="kpi-demo" />
        <Kpi label="Nog te onboarden" value={kpi.onb_needs_help} icon={Building2} data-testid="kpi-onboarding-open" />
        <Kpi label="Totaal kinderen" value={kpi.kids} icon={Baby} data-testid="kpi-total-kids" />
      </div>

      <div className="mb-4">
        <Input
          data-testid="platform-search"
          placeholder="Zoek op naam, slug of organisation_id…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="max-w-md"
        />
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-neutral-500" data-testid="platform-loading">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-neutral-500" data-testid="platform-empty">Geen organisaties gevonden.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(o => (
            <Card key={o.id} className="p-4 bg-white border-neutral-200 hover:border-emerald-200 hover:shadow-sm transition-all" data-testid={`org-card-${o.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-semibold text-neutral-900" data-testid={`org-name-${o.id}`}>{o.name}</div>
                    {o.is_demo && (
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-widest" data-testid={`org-demo-${o.id}`}>Demo</Badge>
                    )}
                    {o.archived && (
                      <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px] uppercase tracking-widest" data-testid={`org-archived-${o.id}`} title={`Gepland voor definitieve verwijdering op ${(o.purge_at || '').slice(0, 10)}`}>
                        <Archive className="h-3 w-3 mr-1 inline" /> Gearchiveerd
                      </Badge>
                    )}
                    <Badge className={
                      "text-[10px] uppercase tracking-widest border " +
                      (o.status === "actief"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-neutral-100 text-neutral-600 border-neutral-200")
                    } data-testid={`org-status-${o.id}`}>{o.status || "onbekend"}</Badge>
                    <Badge className={
                      "text-[10px] uppercase tracking-widest border " +
                      (o.onboarding?.status === "active" || o.onboarding?.status === "actief" || o.onboarding?.status === "completed"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : o.onboarding?.status === "in_progress"
                          ? "bg-sky-50 text-sky-700 border-sky-200"
                          : "bg-amber-50 text-amber-700 border-amber-200")
                    } data-testid={`org-onboarding-${o.id}`}>
                      Onboarding · {o.onboarding?.status || "pending"}
                    </Badge>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1 font-mono">id: {o.id}</div>

                  {/* Onboarding-voortgang — geeft sales in één oogopslag zicht op wie hulp nodig heeft */}
                  {o.onboarding?.progress && (
                    <div className="mt-3" data-testid={`org-progress-${o.id}`}>
                      <div className="flex items-center justify-between text-[11px] text-neutral-600 mb-1">
                        <span className="uppercase tracking-widest">Onboarding-voortgang</span>
                        <span className="font-mono font-semibold" data-testid={`org-progress-num-${o.id}`}>
                          {o.onboarding.progress.done}/{o.onboarding.progress.total} · {o.onboarding.progress.percent}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, Math.max(0, o.onboarding.progress.percent))}%`,
                            background: o.onboarding.progress.percent >= 100
                              ? "#10b981"
                              : o.onboarding.progress.percent >= 50
                                ? "var(--sage)"
                                : o.onboarding.progress.percent > 0
                                  ? "#f59e0b"
                                  : "#e5e5e5",
                          }}
                          data-testid={`org-progress-bar-${o.id}`}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3 text-xs">
                    <Stat icon={MapPin} label="Locaties" value={o.counts?.locations} testid={`org-locs-${o.id}`} />
                    <Stat icon={Users} label="Groepen" value={o.counts?.groups} testid={`org-groups-${o.id}`} />
                    <Stat icon={Baby} label="Kinderen" value={o.counts?.children} testid={`org-kids-${o.id}`} />
                    <Stat icon={FileText} label="Contracten" value={o.counts?.active_contracts} testid={`org-contracts-${o.id}`} />
                    <Stat icon={Users} label="Medewerkers" value={o.counts?.staff} testid={`org-staff-${o.id}`} />
                    <Stat icon={Users} label="Gebruikers" value={o.counts?.active_users} testid={`org-users-${o.id}`} />
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/platform/organisaties/${o.id}`)}
                    data-testid={`org-view-${o.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" /> Bekijken
                  </Button>
                  <Button
                    onClick={() => startSupport(o)}
                    className="text-white"
                    style={{ background: "var(--sage)" }}
                    data-testid={`org-support-${o.id}`}
                  >
                    <LifeBuoy className="h-4 w-4 mr-2" /> Ondersteunen
                  </Button>
                  {!o.is_demo && o.id !== "demo_regenboog" && !o.archived && (
                    <Button
                      variant="outline"
                      onClick={() => deleteOrg(o)}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      data-testid={`org-archive-${o.id}`}
                    >
                      <Archive className="h-4 w-4 mr-2" /> Archiveren
                    </Button>
                  )}
                  {o.archived && (
                    <Button
                      variant="outline"
                      onClick={() => restoreOrg(o)}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid={`org-restore-${o.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-2" /> Herstellen
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/** /platform/organisaties/:organizationId — detailpagina van één KDV. */
export function PlatformOrgDetailPage() {
  const { organizationId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get(`/platform/organizations/${organizationId}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [organizationId]);

  const startSupport = () => {
    setImpersonatedOrg(organizationId);
    toast.success(`Supportmodus gestart — ${data?.organization?.name}`);
    setTimeout(() => { window.location.href = "/app"; }, 200);
  };

  const herstelDemo = async () => {
    if (!window.confirm("Alle Regenboog-demo data wordt gewist en opnieuw geplaatst. Doorgaan?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/demo/seed-regenboog");
      toast.success(`Regenboog-demo hersteld — ${r.data.counts.children} kinderen, ${r.data.counts.medewerkers} medewerkers`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-10 text-sm text-neutral-500" data-testid="org-detail-loading">Laden…</div>;
  if (!data) return <div className="text-center py-10 text-sm text-neutral-500" data-testid="org-detail-empty">Organisatie niet gevonden.</div>;

  const o = data.organization || {};
  return (
    <div data-testid="org-detail">
      <Button
        variant="ghost"
        onClick={() => navigate("/platform/organisaties")}
        className="text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 mb-4"
        data-testid="org-back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Terug naar overzicht
      </Button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-neutral-900" data-testid="org-detail-name">{o.name}</h1>
            {data.is_demo && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-widest">Demo</Badge>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1 font-mono">id: {o.id} · slug: {o.slug || "—"}</div>
        </div>
        <div className="flex gap-2 shrink-0">
          {data.is_demo && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={herstelDemo}
              className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              data-testid="org-demo-reset-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Demo-data herstellen
            </Button>
          )}
          <Button
            onClick={startSupport}
            className="text-white"
            style={{ background: "var(--sage)" }}
            data-testid="org-start-support-btn"
          >
            <LifeBuoy className="h-4 w-4 mr-2" /> Start supportmodus
          </Button>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Kpi label="Locaties" value={data.counts?.locations} icon={MapPin} data-testid="det-kpi-locations" />
        <Kpi label="Groepen" value={data.counts?.groups} icon={Users} data-testid="det-kpi-groups" />
        <Kpi label="Kinderen" value={data.counts?.children} icon={Baby} data-testid="det-kpi-children" />
        <Kpi label="Contracten" value={data.counts?.active_contracts} icon={FileText} data-testid="det-kpi-contracts" />
        <Kpi label="Medewerkers" value={data.counts?.staff} icon={Users} data-testid="det-kpi-staff" />
        <Kpi label="Gebruikers" value={data.counts?.active_users} icon={Users} data-testid="det-kpi-users" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4 bg-white border-neutral-200">
          <div className="text-sm font-semibold mb-3 text-neutral-900">Rolverdeling</div>
          {Object.keys(data.role_breakdown || {}).length === 0 ? (
            <div className="text-xs text-neutral-500">Geen actieve gebruikers</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(data.role_breakdown).map(([role, n]) => (
                <div key={role} className="flex justify-between text-xs" data-testid={`role-${role}`}>
                  <span className="text-neutral-500">{role}</span>
                  <span className="font-semibold text-neutral-900">{n}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4 bg-white border-neutral-200">
          <div className="text-sm font-semibold mb-3 text-neutral-900">Locaties</div>
          {(data.locations || []).length === 0 ? (
            <div className="text-xs text-neutral-500">Geen locaties</div>
          ) : (
            <div className="space-y-1">
              {data.locations.map(l => (
                <div key={l.id} className="text-xs" data-testid={`loc-${l.id}`}>
                  <span className="text-neutral-900">{l.name}</span>
                  <span className="text-neutral-500 ml-2 font-mono">LRK {l.lrk_number || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-4 bg-white border-neutral-200 mt-4">
        <div className="text-sm font-semibold mb-3 text-neutral-900">Recente auditactiviteit</div>
        {(data.recent_audit || []).length === 0 ? (
          <div className="text-xs text-neutral-500">Geen recente activiteit</div>
        ) : (
          <div className="space-y-1 text-xs">
            {data.recent_audit.slice(0, 10).map((a, i) => (
              <div key={a.id || i} className="flex gap-3 border-b border-neutral-100 pb-1" data-testid={`audit-${i}`}>
                <span className="text-neutral-400 font-mono shrink-0">{(a.created_at || a.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-neutral-700">{a.action}</span>
                <span className="text-neutral-500">{a.entity || a.resource_type} {a.entity_id || a.resource_id}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon: Ic, "data-testid": testid }) {
  return (
    <Card className="p-3 bg-white border-neutral-200" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Ic className="h-5 w-5" style={{ color: "var(--sage)" }} />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
          <div className="text-2xl font-semibold text-neutral-900">{value ?? 0}</div>
        </div>
      </div>
    </Card>
  );
}

function Stat({ icon: Ic, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Ic className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
      <span className="text-neutral-500">{label}:</span>
      <span className="font-semibold text-neutral-900">{value ?? 0}</span>
    </div>
  );
}
